import { useMemo, useState } from 'react';
import type { Programme, Rate, RoundingMode } from '@tariff/authoring';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * The two things that belong to a CARD rather than to any one rule: how its
 * prices are rounded, and copying rules in from another accommodation.
 */

/**
 * What an operator may choose. NOT every mode the engine can evaluate.
 *
 * `cents` is absent because it is not a choice: money does not go below the
 * cent, so every mode ends there and `none` says so.
 *
 * `psychological` is absent because it is QPC's, and retired. It pulls a total
 * from 1, 2, 3 and 4 toward a multiple of five and leaves 6 through 9 where
 * they are — which is not a scheme anybody would pick, and this list described
 * it as "….00 or ….50", which it never produced. The engine still evaluates it,
 * because archived cards carry it and an enquiry names the version that priced
 * it; nothing may newly select it.
 *
 * A card that still holds it keeps it until somebody changes the setting, and
 * the control says so rather than silently showing the wrong answer.
 */
const ROUNDING: Array<[RoundingMode, MessageKey]> = [
  ['none', 'rounding.none'],
  ['tenCents', 'rounding.tenCents'],
  ['euro', 'rounding.euro'],
];

/** Modes an operator cannot pick but a card may still be on. */
const RETIRED: Partial<Record<RoundingMode, MessageKey>> = {
  psychological: 'rounding.psychological',
  cents: 'rounding.cents',
};

/**
 * How a card's prices are rounded.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * It already did, twice, and neither was reachable. `Programme.rounding` had no
 * control; and the ported cards carried it instead as a MODIFIER — a surcharge
 * of x1 whose only effect was the rounding, named `Pricefactor#114` by the
 * system it came from.
 *
 * That is what the operator saw: a line in a quote reading "Surcharges:
 * Pricefactor#114" with the rounding difference beside it, no way to find out
 * what it was, and nothing anywhere to set it. Both halves of that question
 * were fair, and the answer to the second was "you cannot".
 *
 * The list an operator sees is deliberately shorter than what the engine can
 * evaluate; see `ROUNDING` for which two are missing and why.
 */
export function RoundingSetting({ programme, readOnly, onChange }: {
  programme: Programme;
  readOnly?: boolean;
  onChange: (next: Programme) => void;
}) {
  const t = useT();
  const value: RoundingMode = programme.rounding ?? 'none';
  return (
    <div className="stack-sm">
      <label className="shape-field">
        {t('rounding.label')}
        <select
          value={value}
          disabled={readOnly}
          onChange={(e) => {
            const mode = e.target.value as RoundingMode;
            const next = { ...programme };
            // `none` is the absence of the field, not a value to store. A card
            // that says "round to nothing" and a card that says nothing about
            // rounding are the same card, and only one of them is worth writing.
            if (mode === 'none') delete (next as { rounding?: unknown }).rounding;
            else next.rounding = mode;
            onChange(next);
          }}
        >
          {ROUNDING.map(([mode, key]) => (
            <option key={mode} value={mode}>{t(key)}</option>
          ))}
          {/* The card's own setting, where it is one nobody may newly choose.
              Listed so the control shows what the card actually does rather
              than snapping it to the first option and looking like the
              operator asked for that. Picking anything else is one-way. */}
          {RETIRED[value] && (
            <option value={value}>{t(RETIRED[value]!)}</option>
          )}
        </select>
      </label>
      {/* Ported cards carried the old calculator's rounding as a surcharge of
          x1 named like "Pricefactor#114". It is this setting and nothing else;
          see the lift in packages/tariff-compat. */}
      <p className="note">{t('rounding.note')}</p>
    </div>
  );
}

/** A card this one could copy rules from. */
export interface CopySource {
  id: string;
  accommodationId: string;
  accommodationLabel: string;
  label: string;
}

/**
 * Copy rules in from another accommodation.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * Because `POST /api/versions/:id/copy-rates` has existed all along, validated
 * and refusing published targets, with a client method in front of it and NO
 * BUTTON ANYWHERE. The operator asked where it was. Twenty-four accommodations
 * share most of their shape — the same ladder, the same weekend rule, the same
 * arrangement — and re-typing that per card is both the slowest part of the job
 * and where the differences nobody meant come from.
 *
 * ---------------------------------------------------------------------------
 * SAME SEASON ONLY, AND WHY THAT IS NOT A LIMITATION
 * ---------------------------------------------------------------------------
 *
 * Rules carry dates. Copying a 2026 rule into a 2027 card brings 2026's dates
 * with it, and a rule that is a year out prices nothing while looking perfectly
 * correct in the list. Carrying a card FORWARD is a different operation, has a
 * different answer — `cloneProgramme`, which shifts every window — and already
 * exists on the versions screen.
 *
 * So the sources offered are this card's own season. The operator's question
 * was about the huisje next door, not about last year.
 */
export function CopyRates({
  target, sources, sourceId, sourceProgramme, busy, onPickSource, onCopy,
}: {
  target: Programme;
  sources: readonly CopySource[];
  sourceId: string;
  /** The chosen source's rules, once fetched. */
  sourceProgramme: Programme | null;
  busy?: boolean;
  onPickSource: (id: string) => void;
  onCopy: (rateIds: string[]) => void;
}) {
  const t = useT();
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const known = useMemo(
    () => new Set((target.quantities ?? []).map((q) => q.key)),
    [target.quantities],
  );
  const existing = useMemo(() => new Set(target.rates.map((r) => r.id)), [target.rates]);

  /**
   * A rule that prices a guest category this card has never heard of cannot be
   * copied — the programme would not compile, and the API answers 422.
   *
   * Said HERE rather than after the attempt. "Copying those rates would leave
   * the programme invalid" is a true sentence that leaves the operator no
   * better off; naming the category, on the rule, before they tick it, tells
   * them what to add on the Guests screen first.
   */
  const blocker = (r: Rate): string | null => {
    const missing = Object.keys(r.price?.perUnit ?? {}).filter((k) => !known.has(k));
    return missing.length ? missing.join(', ') : null;
  };

  const toggle = (id: string) => setPicked((was) => {
    const next = new Set(was);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const rules = sourceProgramme?.rates ?? [];
  const copyable = rules.filter((r) => !blocker(r));
  const chosen = [...picked].filter((id) => copyable.some((r) => r.id === id));
  const overwrites = chosen.filter((id) => existing.has(id));

  return (
    <div className="stack-sm">
      <label className="shape-field">
        {t('copy.from')}
        <select
          value={sourceId}
          disabled={busy}
          onChange={(e) => { setPicked(new Set()); onPickSource(e.target.value); }}
        >
          <option value="">{t('copy.choose')}</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>{s.accommodationLabel}</option>
          ))}
        </select>
      </label>

      {sourceId && sourceProgramme && (
        <>
          {rules.length === 0 && <p className="note">{t('copy.sourceEmpty')}</p>}
          {rules.length > 0 && (
            <>
              <div className="row">
                <button
                  type="button" className="link"
                  onClick={() => setPicked(new Set(copyable.map((r) => r.id)))}
                >
                  {t('copy.selectAll')}
                </button>
                <button type="button" className="link" onClick={() => setPicked(new Set())}>
                  {t('copy.selectNone')}
                </button>
              </div>
              <ul className="copy-list">
                {rules.map((r) => {
                  const why = blocker(r);
                  return (
                    <li key={r.id}>
                      <label className={why ? 'muted' : undefined}>
                        <input
                          type="checkbox"
                          checked={picked.has(r.id)}
                          disabled={!!why || busy}
                          onChange={() => toggle(r.id)}
                        />
                        <span className="grow">
                          {r.label ?? r.id}
                          <span className="meta mono">{r.id}</span>
                        </span>
                        {why
                          ? <span className="chip bad">{t('copy.needsQuantity', { keys: why })}</span>
                          : existing.has(r.id)
                            ? <span className="chip">{t('copy.willReplace')}</span>
                            : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
              {overwrites.length > 0 && (
                <p className="note">{t('copy.replacing', { count: overwrites.length })}</p>
              )}
              <button
                type="button" className="btn primary"
                disabled={chosen.length === 0 || busy}
                onClick={() => { onCopy(chosen); setPicked(new Set()); }}
              >
                {t('copy.go', { count: chosen.length })}
              </button>
              {/* Dates come across unchanged, which is the whole reason the
                  source list is this season only. Said anyway: the rules being
                  copied were written for a different accommodation, and their
                  windows are as much a decision as their prices. */}
              <p className="note">{t('copy.datesNote')}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
